import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import LoadingScreen from '../components/ui/LoadingScreen';
import Modal from '../components/ui/Modal';
import EmptyState from '../components/ui/EmptyState';
import StatusBadge from '../components/ui/StatusBadge';
import { IconPlus } from '../components/ui/Icons';
import { getProductId } from '../utils/ids';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';

const emptyForm = {
  name: '',
  isDefault: false,
  smtpHost: '',
  smtpPort: 587,
  smtpSecure: false,
  smtpUser: '',
  smtpPassword: '',
  fromAddress: '',
  fromName: '',
};

function EmailConfig() {
  const toast = useToast();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [products, setProducts] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(searchParams.get('productId') || '');
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (!loading && selectedProductId) {
      fetchConfigs();
    } else if (!loading) {
      setConfigs([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, selectedProductId]);

  const fetchProducts = async () => {
    try {
      const response = await api.post('/product/list', {});
      const productsList = response.data.data?.products || [];
      setProducts(productsList);

      const fromUrl = searchParams.get('productId');
      if (fromUrl && productsList.some((p) => getProductId(p) === fromUrl)) {
        setSelectedProductId(fromUrl);
      } else if (!selectedProductId && productsList.length > 0) {
        setSelectedProductId(getProductId(productsList[0]));
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfigs = async () => {
    if (!selectedProductId) return;
    try {
      const response = await api.post('/admin/email-config/list', {
        productId: selectedProductId,
      });
      setConfigs(response.data.data?.configs || []);
    } catch (error) {
      console.error('Error fetching email configs:', error);
      toast.error('Failed to load email configurations');
      setConfigs([]);
    }
  };

  const handleProductChange = (e) => {
    const productId = e.target.value;
    setSelectedProductId(productId);
    if (productId) {
      setSearchParams({ productId });
    } else {
      setSearchParams({});
    }
  };

  const openCreateModal = () => {
    setEditingConfig(null);
    setForm({
      ...emptyForm,
      isDefault: configs.length === 0,
    });
    setShowModal(true);
  };

  const openEditModal = (config) => {
    setEditingConfig(config);
    setForm({
      name: config.name || '',
      isDefault: !!config.isDefault,
      smtpHost: config.smtpHost || '',
      smtpPort: config.smtpPort || 587,
      smtpSecure: config.smtpSecure || false,
      smtpUser: config.smtpUser || '',
      smtpPassword: '',
      fromAddress: config.fromAddress || '',
      fromName: config.fromName || '',
    });
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value, 10) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingConfig) {
        const payload = {
          configId: editingConfig._id,
          name: form.name,
          isDefault: form.isDefault,
          smtpHost: form.smtpHost,
          smtpPort: form.smtpPort,
          smtpSecure: form.smtpSecure,
          smtpUser: form.smtpUser,
          fromAddress: form.fromAddress,
          fromName: form.fromName || null,
        };
        if (form.smtpPassword) {
          payload.smtpPassword = form.smtpPassword;
        }
        await api.post('/admin/email-config/update', payload);
        toast.success('Email configuration updated');
      } else {
        await api.post('/admin/email-config/create', {
          productId: selectedProductId,
          name: form.name,
          isDefault: form.isDefault,
          smtpHost: form.smtpHost,
          smtpPort: form.smtpPort,
          smtpSecure: form.smtpSecure,
          smtpUser: form.smtpUser,
          smtpPassword: form.smtpPassword,
          fromAddress: form.fromAddress,
          fromName: form.fromName || null,
        });
        toast.success('Email configuration created');
      }
      setShowModal(false);
      fetchConfigs();
    } catch (error) {
      console.error('Error saving email config:', error);
      toast.error(error.response?.data?.message || 'Failed to save email configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (config) => {
    const confirmed = await confirm({
      title: 'Delete email configuration',
      message: `Delete "${config.name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await api.post('/admin/email-config/delete', { configId: config._id });
      toast.success('Email configuration deleted');
      fetchConfigs();
    } catch (error) {
      console.error('Error deleting email config:', error);
      toast.error(error.response?.data?.message || 'Failed to delete email configuration');
    }
  };

  const handleTest = async (config) => {
    setTestingId(config._id);
    try {
      const response = await api.post('/admin/email-config/test', {
        configId: config._id,
      });
      const isValid = response.data.data?.valid;
      if (isValid) {
        toast.success(`"${config.name}" configuration is valid`);
      } else {
        toast.error(`"${config.name}" is missing required SMTP fields`);
      }
    } catch (error) {
      console.error('Error testing email config:', error);
      toast.error(error.response?.data?.message || 'Failed to test configuration');
    } finally {
      setTestingId(null);
    }
  };

  const handleSetDefault = async (config) => {
    if (config.isDefault) return;
    try {
      await api.post('/admin/email-config/update', {
        configId: config._id,
        isDefault: true,
      });
      toast.success(`"${config.name}" is now the default`);
      fetchConfigs();
    } catch (error) {
      console.error('Error setting default:', error);
      toast.error(error.response?.data?.message || 'Failed to set default configuration');
    }
  };

  const selectedProduct = products.find((p) => getProductId(p) === selectedProductId);

  if (loading) {
    return <LoadingScreen message="Loading email configuration..." />;
  }

  return (
    <div className="page">
      <PageHeader
        title="Email Configuration"
        description="Manage multiple SMTP configurations per product. The default config is used for OTP and welcome emails."
        action={
          selectedProductId ? (
            <button type="button" onClick={openCreateModal} className="btn btn--primary">
              <IconPlus width={18} height={18} />
              Add Configuration
            </button>
          ) : null
        }
      />

      <div className="card card__body">
        <div className="form-group">
          <label htmlFor="productId" className="form-label">
            Product <span className="required">*</span>
          </label>
          <select
            id="productId"
            className="form-select"
            value={selectedProductId}
            onChange={handleProductChange}
            required
          >
            <option value="">Select a product</option>
            {products.map((product) => {
              const productId = getProductId(product);
              return (
                <option key={productId} value={productId}>
                  {product.name}
                </option>
              );
            })}
          </select>
          <span className="form-hint">
            Choose a product to view and manage its email configurations.
          </span>
        </div>
      </div>

      {!selectedProductId ? (
        <div className="card">
          <EmptyState
            title="Select a product"
            description="Pick a product above to manage its SMTP configurations."
          />
        </div>
      ) : configs.length === 0 ? (
        <div className="card">
          <EmptyState
            title="No email configurations"
            description={`Create an SMTP configuration for ${selectedProduct?.name || 'this product'}.`}
            action={
              <button type="button" onClick={openCreateModal} className="btn btn--primary">
                <IconPlus width={18} height={18} />
                Add Configuration
              </button>
            }
          />
        </div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
            <table className="data-table data-table--responsive">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>From</th>
                  <th>SMTP Host</th>
                  <th>Default</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {configs.map((config) => (
                  <tr key={config._id}>
                    <td data-label="Name">{config.name}</td>
                    <td data-label="From">
                      {config.fromName ? `${config.fromName} ` : ''}
                      &lt;{config.fromAddress}&gt;
                    </td>
                    <td data-label="SMTP Host" className="cell-mono">
                      {config.smtpHost}:{config.smtpPort}
                    </td>
                    <td data-label="Default">
                      {config.isDefault ? (
                        <StatusBadge status="Default" />
                      ) : (
                        <button
                          type="button"
                          className="btn btn--secondary btn--sm"
                          onClick={() => handleSetDefault(config)}
                        >
                          Set default
                        </button>
                      )}
                    </td>
                    <td data-label="Updated">
                      {config.updatedAt
                        ? new Date(config.updatedAt).toLocaleDateString()
                        : '—'}
                    </td>
                    <td data-label="Actions" className="cell-actions">
                      <div className="btn-group">
                        <button
                          type="button"
                          className="btn btn--secondary btn--sm"
                          onClick={() => openEditModal(config)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn--secondary btn--sm"
                          onClick={() => handleTest(config)}
                          disabled={testingId === config._id}
                        >
                          {testingId === config._id ? 'Testing...' : 'Test'}
                        </button>
                        <button
                          type="button"
                          className="btn btn--danger btn--sm"
                          onClick={() => handleDelete(config)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <Modal
          title={editingConfig ? 'Edit Email Configuration' : 'New Email Configuration'}
          subtitle={selectedProduct?.name}
          onClose={() => setShowModal(false)}
          size="lg"
        >
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                Configuration name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className="form-input"
                value={form.name}
                onChange={handleFormChange}
                required
                placeholder="e.g. Primary SMTP, Marketing"
              />
            </div>

            <div className="form-group">
              <label htmlFor="isDefault" className="form-checkbox">
                <input
                  type="checkbox"
                  id="isDefault"
                  name="isDefault"
                  checked={form.isDefault}
                  onChange={handleFormChange}
                />
                <span>Use as default for this product (OTP &amp; welcome emails)</span>
              </label>
            </div>

            <div className="form-section">
              <h3 className="form-section__title">SMTP Server</h3>
              <div className="form-group">
                <label htmlFor="smtpHost" className="form-label">
                  SMTP Host <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="smtpHost"
                  name="smtpHost"
                  className="form-input"
                  value={form.smtpHost}
                  onChange={handleFormChange}
                  required
                  placeholder="smtp.gmail.com"
                />
              </div>
              <div className="form-group">
                <label htmlFor="smtpPort" className="form-label">
                  SMTP Port <span className="required">*</span>
                </label>
                <input
                  type="number"
                  id="smtpPort"
                  name="smtpPort"
                  className="form-input"
                  value={form.smtpPort}
                  onChange={handleFormChange}
                  required
                  min="1"
                  max="65535"
                />
              </div>
              <div className="form-group">
                <label htmlFor="smtpSecure" className="form-checkbox">
                  <input
                    type="checkbox"
                    id="smtpSecure"
                    name="smtpSecure"
                    checked={form.smtpSecure}
                    onChange={handleFormChange}
                  />
                  <span>Use secure connection (SSL/TLS)</span>
                </label>
              </div>
              <div className="form-group">
                <label htmlFor="smtpUser" className="form-label">
                  SMTP Username <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="smtpUser"
                  name="smtpUser"
                  className="form-input"
                  value={form.smtpUser}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="smtpPassword" className="form-label">
                  SMTP Password {!editingConfig && <span className="required">*</span>}
                </label>
                <input
                  type="password"
                  id="smtpPassword"
                  name="smtpPassword"
                  className="form-input"
                  value={form.smtpPassword}
                  onChange={handleFormChange}
                  required={!editingConfig}
                  placeholder={editingConfig ? 'Leave blank to keep unchanged' : ''}
                />
              </div>
            </div>

            <div className="form-section">
              <h3 className="form-section__title">Sender</h3>
              <div className="form-group">
                <label htmlFor="fromAddress" className="form-label">
                  From Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="fromAddress"
                  name="fromAddress"
                  className="form-input"
                  value={form.fromAddress}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="fromName" className="form-label">From Name</label>
                <input
                  type="text"
                  id="fromName"
                  name="fromName"
                  className="form-input"
                  value={form.fromName}
                  onChange={handleFormChange}
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn--secondary" onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button type="submit" className="btn btn--primary" disabled={saving}>
                {saving ? 'Saving...' : editingConfig ? 'Save Changes' : 'Create Configuration'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default EmailConfig;
